import * as THREE from 'three';
import {
  ENVIRONMENT_ID,
  ENVIRONMENTS,
  FLOOR_SIZE_M,
  type EnvironmentDef,
  type PassthroughEnvironmentDef,
  type VirtualEnvironmentDef,
} from '../config/constants';

/**
 * Resolve an environment id to its definition. Tolerates unknown ids (e.g. a
 * remote settings blob written by a newer client) by falling back to forest.
 */
export function getEnvironmentDef(id: string): EnvironmentDef {
  return (
    (ENVIRONMENTS as Record<string, EnvironmentDef>)[id] ?? ENVIRONMENTS[ENVIRONMENT_ID.forest]
  );
}

/**
 * Walkable floor footprint (m). Indoors the plane fills the room box exactly
 * (width × depth) so the textured floor covers the whole walkable area right up
 * to the walls; outdoors (no room box) it stays a FLOOR_SIZE_M square.
 */
function floorSize(def: VirtualEnvironmentDef): { width: number; depth: number } {
  if (def.roomProjection) {
    return { width: def.roomProjection.width, depth: def.roomProjection.depth };
  }
  return { width: FLOOR_SIZE_M, depth: FLOOR_SIZE_M };
}

/**
 * Build the floor mesh with a fallback PBR material.
 * Textures are loaded asynchronously and swapped in via `loadEnvironment`.
 */
export function createFloor(def: VirtualEnvironmentDef): THREE.Mesh {
  const mat = new THREE.MeshStandardMaterial({
    color: def.floor.color,
    roughness: def.floor.roughness,
    metalness: 0,
  });
  const { width, depth } = floorSize(def);
  const floor = new THREE.Mesh(new THREE.PlaneGeometry(width, depth), mat);
  floor.rotation.x = -Math.PI / 2;
  floor.receiveShadow = true;
  return floor;
}

/**
 * Kick off async loading of the environment's HDRI + floor textures. Only the
 * selected environment's assets are fetched (lazy — never both).
 * Resolves once everything is applied; rejects only on programmer errors —
 * asset 404s are caught and logged so the app keeps the fallback look.
 */
export async function loadEnvironment(
  scene: THREE.Scene,
  renderer: THREE.WebGLRenderer,
  floor: THREE.Mesh,
  def: VirtualEnvironmentDef,
): Promise<void> {
  scene.background = new THREE.Color(def.bgColor);

  const hdriPromise = loadHdri(renderer, def.hdriUrl)
    .then(({ envMap, equirect }) => {
      // IBL always comes from the PMREM env map, regardless of how the
      // background is rendered.
      scene.environment = envMap;
      scene.environmentIntensity = def.envIntensity;
      if (def.roomProjection) {
        addRoomProjection(scene, equirect, def.roomProjection);
      } else {
        scene.background = envMap;
        equirect.dispose();
      }
    })
    .catch((err) => {
      console.warn('[environment] HDRI load failed, keeping solid background', err);
    });

  const floorPromise = loadFloorTextures(def)
    .then((maps) => upgradeFloorMaterial(floor, maps, def))
    .catch((err) => {
      console.warn('[environment] floor textures failed, keeping flat color', err);
    });

  await Promise.all([hdriPromise, floorPromise]);
}

/**
 * Passthrough setup: leave the scene background transparent so the headset composites the
 * real surroundings (immersive-ar) behind it, and load the HDRI for image-based lighting
 * only — no background, no floor, no room box. Resolves once IBL is applied (or immediately
 * on an HDRI failure, falling back to the scene lights).
 */
export async function loadPassthroughEnvironment(
  scene: THREE.Scene,
  renderer: THREE.WebGLRenderer,
  def: PassthroughEnvironmentDef,
): Promise<void> {
  scene.background = null;
  try {
    const { envMap, equirect } = await loadHdri(renderer, def.hdriUrl);
    scene.environment = envMap;
    scene.environmentIntensity = def.envIntensity;
    equirect.dispose();
  } catch (err) {
    console.warn('[environment] passthrough IBL load failed, keeping scene lights only', err);
  }
}

type HdriMaps = {
  /** PMREM-filtered map for IBL (`scene.environment`). */
  envMap: THREE.Texture;
  /** Raw equirectangular texture — needed by GroundedSkybox; dispose if unused. */
  equirect: THREE.Texture;
};

async function loadHdri(renderer: THREE.WebGLRenderer, url: string): Promise<HdriMaps> {
  // Only needed once a VR session starts — lazy import keeps it out of the
  // landing-page chunk.
  const { RGBELoader } = await import('three/examples/jsm/loaders/RGBELoader.js');
  return new Promise((resolve, reject) => {
    const loader = new RGBELoader();
    loader.load(
      url,
      (texture) => {
        const pmrem = new THREE.PMREMGenerator(renderer);
        pmrem.compileEquirectangularShader();
        const envMap = pmrem.fromEquirectangular(texture).texture;
        pmrem.dispose();
        resolve({ envMap, equirect: texture });
      },
      undefined,
      (err) => reject(new Error(`Failed to load environment map: ${url}`, { cause: err })),
    );
  });
}

const ROOM_PROJECTION_VERTEX = /* glsl */ `
  varying vec3 vWorldPosition;
  void main() {
    vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// Samples the equirect with three's own mapping (equirectUv) so the visible
// room stays consistent with the PMREM-based IBL reflections.
const ROOM_PROJECTION_FRAGMENT = /* glsl */ `
  uniform sampler2D map;
  uniform vec3 projectionCenter;
  varying vec3 vWorldPosition;
  #define RECIPROCAL_PI 0.3183098861837907
  #define RECIPROCAL_PI2 0.15915494309189535
  void main() {
    vec3 dir = normalize(vWorldPosition - projectionCenter);
    vec2 uv = vec2(
      atan(dir.z, dir.x) * RECIPROCAL_PI2 + 0.5,
      asin(clamp(dir.y, -1.0, 1.0)) * RECIPROCAL_PI + 0.5
    );
    gl_FragColor = texture2D(map, uv);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

/**
 * Render the HDRI projected onto a room-sized box instead of `scene.background`:
 * each fragment samples the equirect along its direction from the point the
 * photo was taken, so floor, ceiling and flat walls sit at their real depth and
 * keep correct parallax as the player moves. (An equirect background renders at
 * infinity and "floats"; a ground-projected sphere warps the walls indoors.)
 */
function addRoomProjection(
  scene: THREE.Scene,
  equirect: THREE.Texture,
  room: NonNullable<VirtualEnvironmentDef['roomProjection']>,
): void {
  const material = new THREE.ShaderMaterial({
    uniforms: {
      map: { value: equirect },
      projectionCenter: { value: new THREE.Vector3(0, room.cameraHeight, 0) },
    },
    vertexShader: ROOM_PROJECTION_VERTEX,
    fragmentShader: ROOM_PROJECTION_FRAGMENT,
    side: THREE.BackSide,
  });
  const box = new THREE.Mesh(new THREE.BoxGeometry(room.width, room.height, room.depth), material);
  // Sink 1cm so the box floor sits just below the local floor plane (no
  // z-fighting; the textured plane wins where they overlap).
  box.position.y = room.height / 2 - 0.01;
  scene.add(box);
}

type FloorMaps = {
  color: THREE.Texture;
  normal: THREE.Texture;
  rough: THREE.Texture;
};

function loadFloorTextures(def: VirtualEnvironmentDef): Promise<FloorMaps> {
  const loader = new THREE.TextureLoader();
  const load = (url: string): Promise<THREE.Texture> =>
    new Promise((resolve, reject) => {
      loader.load(url, resolve, undefined, reject);
    });

  return Promise.all([
    load(def.floor.colorUrl),
    load(def.floor.normalUrl),
    load(def.floor.roughUrl),
  ]).then(([color, normal, rough]) => ({ color, normal, rough }));
}

function upgradeFloorMaterial(
  floor: THREE.Mesh,
  maps: FloorMaps,
  def: VirtualEnvironmentDef,
): void {
  // `def.floor.repeat` is authored as tiles across FLOOR_SIZE_M, i.e. a texel
  // density (repeats per metre). Re-apply that density to the actual footprint so
  // a floor widened to fill the room tiles the texture instead of stretching it.
  const { width, depth } = floorSize(def);
  const density = def.floor.repeat / FLOOR_SIZE_M;
  for (const tex of [maps.color, maps.normal, maps.rough]) {
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(density * width, density * depth);
  }
  maps.color.colorSpace = THREE.SRGBColorSpace;

  const mat = floor.material as THREE.MeshStandardMaterial;
  mat.map = maps.color;
  mat.normalMap = maps.normal;
  mat.roughnessMap = maps.rough;
  mat.color.setHex(0xffffff);
  mat.needsUpdate = true;
}
